import React, { useState, useEffect, useRef, useCallback } from "react";
import { IoSearch, IoClose } from "react-icons/io5";
import { toast } from "react-toastify";
import { CenterModal } from "../componants/UI/ModalShell";
import {
  useGetParticipantsQuery,
  useDistributeParticipantMutation,
  useGetOrdersQuery,
  useCreateOrderMutation,
} from "../redux/appSlices/bmciEvent/bmciEventApiSlice";

const LIMIT = 30;

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function BmciEvent() {
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [page, setPage] = useState(1);
  const [participants, setParticipants] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderForm, setOrderForm] = useState({ first_name: "", last_name: "", company: "", email: "", phone: "" });
  const sentinelRef = useRef(null);

  const { data, isFetching } = useGetParticipantsQuery({
    search: debouncedSearch,
    page,
    limit: LIMIT,
  });

  const [distributeParticipant] = useDistributeParticipantMutation();
  const { data: ordersData, isFetching: isFetchingOrders } = useGetOrdersQuery();
  const [createOrder, { isLoading: isCreatingOrder }] = useCreateOrderMutation();

  // Reset list when search changes
  useEffect(() => {
    setPage(1);
    setParticipants([]);
    setHasNextPage(true);
  }, [debouncedSearch]);

  // Append new page data
  useEffect(() => {
    if (!data?.data?.participants) return;
    if (page === 1) {
      setParticipants(data.data.participants);
    } else {
      setParticipants((prev) => [...prev, ...data.data.participants]);
    }
    setHasNextPage(data.pagination?.hasNextPage ?? false);
  }, [data]);

  // IntersectionObserver for infinite scroll
  const handleObserver = useCallback(
    (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetching) {
        setPage((p) => p + 1);
      }
    },
    [hasNextPage, isFetching]
  );

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleObserver]);

  const handleToggleDistribute = async (participant) => {
    try {
      await distributeParticipant(participant._id).unwrap();
      setParticipants((prev) =>
        prev.map((p) =>
          p._id === participant._id
            ? { ...p, distributed: !p.distributed, distributed_at: !p.distributed ? new Date().toISOString() : undefined }
            : p
        )
      );
    } catch {
      toast.error("Une erreur est survenue");
    }
  };

  const handleOrderChange = (e) => {
    setOrderForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCreateOrder = async () => {
    if (!orderForm.first_name.trim() || !orderForm.last_name.trim()) {
      toast.error("Prénom et nom sont obligatoires");
      return;
    }
    try {
      await createOrder({
        first_name: orderForm.first_name.trim(),
        last_name: orderForm.last_name.trim(),
        company: orderForm.company.trim(),
        email: orderForm.email.trim(),
        phone: orderForm.phone.trim(),
      }).unwrap();
      toast.success("Demande enregistrée");
      setOrderForm({ first_name: "", last_name: "", company: "", email: "", phone: "" });
      setShowOrderModal(false);
    } catch {
      toast.error("Une erreur est survenue");
    }
  };

  const distributedCount = participants.filter((p) => p.distributed).length;

  return (
    <div className="min-h-[100vh] bg-gray-50 font-sans">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="mx-auto max-w-2xl px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-gray-900">BMCI Event</h1>
              <p className="text-xs text-gray-500">
                {distributedCount}/{participants.length > 0 ? (data?.pagination?.total ?? participants.length) : "..."} distribués
              </p>
            </div>
          </div>
          {/* Tab bar */}
          <div className="mt-3 flex rounded-xl bg-gray-100 p-1">
            {["Distribution", "Nouvelles commandes"].map((label, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActiveTab(i)}
                className={`flex flex-auto items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  activeTab === i
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {label}
                {i === 1 && ordersData?.data?.orders?.length > 0 && (
                  <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-gray-900 px-1 text-[10px] text-white">
                    {ordersData.data.orders.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab 1: Distribution */}
      {activeTab === 0 && (
        <div className="mx-auto max-w-2xl px-4 pt-4 pb-8">
          {/* Search + CTA */}
          <div className="mb-3 flex gap-2">
            <div className="relative flex-1">
              <IoSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par ID ou nom..."
                className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-9 text-sm outline-none focus:border-gray-400"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  <IoClose className="h-4 w-4" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowOrderModal(true)}
              className="shrink-0 rounded-xl bg-gray-900 px-3 py-2.5 text-sm font-medium text-white hover:bg-black"
            >
              + Demande
            </button>
          </div>

          {/* Table */}
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            {/* Header row */}
            <div className="grid grid-cols-[44px_1fr_90px] gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <span>ID</span>
              <span>Nom complet</span>
              <span className="text-right">Distribué</span>
            </div>

            {participants.length === 0 && !isFetching && (
              <div className="py-12 text-center text-sm text-gray-400">
                Aucun résultat
              </div>
            )}

            {participants.map((p) => (
              <div
                key={p._id}
                className={`grid grid-cols-[44px_1fr_90px] gap-2 border-b border-gray-50 px-3 py-3 text-sm last:border-b-0 ${
                  p.distributed ? "opacity-40" : ""
                }`}
              >
                <span className="font-mono text-xs font-semibold text-gray-700">
                  {p.sticker_id}
                </span>
                <span className="font-medium text-gray-900">{p.full_name}</span>
                <div className="flex justify-end">
                  <input
                    type="checkbox"
                    checked={p.distributed}
                    onChange={() => handleToggleDistribute(p)}
                    className="h-5 w-5 cursor-pointer accent-gray-900"
                  />
                </div>
              </div>
            ))}

            {/* Sentinel for infinite scroll */}
            <div ref={sentinelRef} className="h-4" />

            {isFetching && (
              <div className="py-4 text-center text-xs text-gray-400">
                Chargement...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab 2: Nouvelles commandes */}
      {activeTab === 1 && (
        <div className="mx-auto max-w-2xl px-4 pt-4 pb-8">
          {isFetchingOrders ? (
            <div className="py-12 text-center text-sm text-gray-400">Chargement...</div>
          ) : !ordersData?.data?.orders?.length ? (
            <div className="rounded-2xl border border-gray-200 bg-white py-16 text-center text-sm text-gray-400">
              Aucune commande pour l'instant
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
              <div className="grid grid-cols-3 gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <span>Prénom</span>
                <span>Nom</span>
                <span>Entreprise</span>
              </div>
              {ordersData.data.orders.map((o) => (
                <div
                  key={o._id}
                  onClick={() => setSelectedOrder(o)}
                  className="grid grid-cols-3 gap-2 cursor-pointer border-b border-gray-50 px-3 py-3 text-sm last:border-b-0 active:bg-gray-50"
                >
                  <span className="truncate text-gray-800">{o.first_name}</span>
                  <span className="truncate font-medium text-gray-900">{o.last_name}</span>
                  <span className="truncate text-gray-500">{o.company || "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Order detail modal */}
      <CenterModal
        isOpen={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        maxWidth="max-w-sm"
      >
        {selectedOrder && (
          <div className="p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-[17px] font-semibold text-gray-900">Détail de la commande</h2>
              <button
                type="button"
                onClick={() => setSelectedOrder(null)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100"
              >
                <IoClose className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-col gap-3">
              {[
                { label: "Prénom", value: selectedOrder.first_name },
                { label: "Nom", value: selectedOrder.last_name },
                { label: "Entreprise", value: selectedOrder.company || "—" },
                { label: "Email", value: selectedOrder.email || "—" },
                { label: "Téléphone", value: selectedOrder.phone || "—" },
                { label: "Date", value: formatDate(selectedOrder.createdAt) },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between gap-4 border-b border-gray-50 pb-3 last:border-b-0 last:pb-0">
                  <span className="text-sm text-gray-500">{label}</span>
                  <span className="text-sm font-medium text-gray-900 text-right">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CenterModal>

      {/* Nouvelle demande modal */}
      <CenterModal
        isOpen={showOrderModal}
        onClose={() => setShowOrderModal(false)}
        maxWidth="max-w-md"
      >
        <div className="p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-[17px] font-semibold text-gray-900">Nouvelle demande</h2>
            <button
              type="button"
              onClick={() => setShowOrderModal(false)}
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100"
            >
              <IoClose className="h-5 w-5" />
            </button>
          </div>
          <div className="flex flex-col gap-3 pb-2">
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-gray-700">
                  Prénom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={orderForm.first_name}
                  onChange={handleOrderChange}
                  placeholder="Prénom"
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-gray-400"
                />
              </div>
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-gray-700">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={orderForm.last_name}
                  onChange={handleOrderChange}
                  placeholder="Nom"
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-gray-400"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Entreprise
              </label>
              <input
                type="text"
                name="company"
                value={orderForm.company}
                onChange={handleOrderChange}
                placeholder="Entreprise (optionnel)"
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-gray-400"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={orderForm.email}
                onChange={handleOrderChange}
                placeholder="Email (optionnel)"
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-gray-400"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Téléphone
              </label>
              <input
                type="tel"
                name="phone"
                value={orderForm.phone}
                onChange={handleOrderChange}
                placeholder="Téléphone (optionnel)"
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-gray-400"
              />
            </div>
            <button
              type="button"
              onClick={handleCreateOrder}
              disabled={isCreatingOrder}
              className="mt-2 w-full rounded-xl bg-gray-900 py-3 text-sm font-semibold text-white hover:bg-black disabled:opacity-50"
            >
              {isCreatingOrder ? "Enregistrement..." : "Confirmer"}
            </button>
          </div>
        </div>
      </CenterModal>
    </div>
  );
}
